import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, firstValueFrom, map, of, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { resolveApiBaseUrl } from '@core/api/api-base-url';
import { AuthSessionService } from './auth-session.service';
import { LocalProfileService } from '@shared/services/local-profile.service';
import {
  BasketSummary,
  PendingGroupInvite,
  ShoppingBasket,
  ShoppingGroupDetails,
  UserProfile
} from '@core/models/models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly localProfile = inject(LocalProfileService);
  private readonly authSession = inject(AuthSessionService);
  private readonly baseUrl = resolveApiBaseUrl(
    globalThis.location?.hostname ?? 'localhost',
    environment.apiBaseUrl,
    globalThis.location?.protocol ?? 'http:'
  );

  private readonly currentUserSubject = new BehaviorSubject<UserProfile | null>(null);
  readonly currentUser$ = this.currentUserSubject.asObservable();
  readonly isAdmin$ = this.currentUserSubject.pipe(map((u) => !!u?.isAdmin));

  private readonly sessionCheckedSubject = new BehaviorSubject<boolean>(false);
  readonly sessionChecked$ = this.sessionCheckedSubject.asObservable();

  private readonly activeModalSubject = new BehaviorSubject<'login' | 'profile' | 'groups' | null>(null);
  readonly activeModal$ = this.activeModalSubject.asObservable();

  private authConfigPromise: Promise<{ clientId: string; publicBaseUrl?: string }> | null = null;
  publicBaseUrl = '';

  async getAuthConfig(): Promise<{ clientId: string; publicBaseUrl?: string }> {
    if (!this.authConfigPromise) {
      this.authConfigPromise = firstValueFrom(
        this.http.get<{ clientId: string; publicBaseUrl?: string }>(`${this.baseUrl}/api/auth/config`).pipe(
          tap((cfg) => {
            if (cfg?.publicBaseUrl) {
              this.publicBaseUrl = cfg.publicBaseUrl.trim().replace(/\/+$/, '');
            }
          }),
          catchError(() => of({ clientId: '', publicBaseUrl: '' }))
        )
      );
    }
    const res = await this.authConfigPromise;
    if (res?.publicBaseUrl) {
      this.publicBaseUrl = res.publicBaseUrl.trim().replace(/\/+$/, '');
    }
    return res;
  }

  openLoginModal(): void {
    this.activeModalSubject.next('login');
  }

  openProfileModal(): void {
    this.activeModalSubject.next('profile');
  }

  openGroupsModal(): void {
    this.activeModalSubject.next('groups');
  }

  closeModal(): void {
    this.activeModalSubject.next(null);
  }

  get currentUser(): UserProfile | null {
    return this.currentUserSubject.value;
  }

  get isLoggedIn(): boolean {
    return this.currentUserSubject.value != null;
  }

  get isAdmin(): boolean {
    return !!this.currentUserSubject.value?.isAdmin;
  }

  constructor() {
    this.authSession.sessionExpired$.subscribe(() => {
      if (this.currentUserSubject.value != null) {
        this.localProfile.clearStoreListBasketId();
        this.currentUserSubject.next(null);
        this.localProfile.clearShoppingBasketId();
      }
    });
    void this.checkSession();
  }

  /** Error from a redirect-mode Google sign-in, shown by the login modal. */
  pendingLoginError = '';

  async checkSession(): Promise<UserProfile | null> {
    const redirectResult = this.consumeRedirectLoginParams();
    try {
      const user = await firstValueFrom(
        this.http.get<UserProfile>(`${this.baseUrl}/api/me`).pipe(
          catchError(() => of(null))
        )
      );
      this.currentUserSubject.next(user);
      this.sessionCheckedSubject.next(true);
      if (redirectResult === 'ok' && user) {
        this.applyLoggedInBasket(user);
      } else if (redirectResult && redirectResult !== 'ok') {
        this.pendingLoginError = redirectResult;
        this.openLoginModal();
      }
      return user;
    } catch {
      this.currentUserSubject.next(null);
      this.sessionCheckedSubject.next(true);
      return null;
    }
  }

  /**
   * Reads and strips ?login=google / ?login_error=... set by the backend after a
   * redirect-mode Google sign-in. Returns 'ok', a user-facing error, or null.
   */
  private consumeRedirectLoginParams(): string | null {
    if (typeof window === 'undefined') return null;
    const url = new URL(window.location.href);
    const ok = url.searchParams.get('login') === 'google';
    const error = url.searchParams.get('login_error');
    if (!ok && !error) return null;

    url.searchParams.delete('login');
    url.searchParams.delete('login_error');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);

    if (ok) return 'ok';
    return error === 'conflict'
      ? 'כתובת האימייל כבר משויכת לחשבון אחר.'
      : 'אירעה שגיאה בהתחברות באמצעות Google. נסו שוב.';
  }

  private applyLoggedInBasket(user: UserProfile): void {
    if (user.currentBasketId != null) {
      this.localProfile.setShoppingBasketId(user.currentBasketId);
    } else {
      this.localProfile.clearShoppingBasketId();
    }
    this.localProfile.setShoppingBasketClaimToken(null);

    // Sync preferred branch if local device hasn't set one yet
    if (user.preferredBranchId != null && !this.localProfile.getSelectedBranchId()) {
      this.localProfile.setSelectedBranchId(user.preferredBranchId);
      if (user.preferredCity) {
        this.localProfile.setSelectedCity(user.preferredCity);
      }
    }
  }

  async loginWithGoogle(
    idToken: string,
    deviceBasketId?: number | null,
    deviceBasketClaimToken?: string | null
  ): Promise<UserProfile> {
    const user = await firstValueFrom(
      this.http.post<UserProfile>(`${this.baseUrl}/api/auth/google`, {
        idToken,
        deviceBasketId: deviceBasketId ?? null,
        deviceBasketClaimToken: deviceBasketClaimToken ?? null
      })
    );
    this.currentUserSubject.next(user);
    this.applyLoggedInBasket(user);

    return user;
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${this.baseUrl}/api/auth/logout`, {}));
    } finally {
      this.localProfile.clearStoreListBasketId();
      this.currentUserSubject.next(null);
      // Clear shopping basket from local profile so a signed-out guest doesn't keep private basket access
      this.localProfile.clearShoppingBasketId();
    }
  }

  async updateProfile(data: {
    givenName?: string;
    familyName?: string;
    defaultGroupId?: number;
    currentBasketId?: number;
    preferredBranchId?: number | null;
    preferredCity?: string | null;
  }): Promise<UserProfile> {
    const updated = await firstValueFrom(
      this.http.patch<UserProfile>(`${this.baseUrl}/api/me`, data)
    );
    this.currentUserSubject.next(updated);
    return updated;
  }

  // ── Groups ─────────────────────────────────────────────────────────────

  getGroups(): Observable<ShoppingGroupDetails[]> {
    return this.http.get<ShoppingGroupDetails[]>(`${this.baseUrl}/api/groups`);
  }

  getGroup(groupId: number): Observable<ShoppingGroupDetails> {
    return this.http.get<ShoppingGroupDetails>(`${this.baseUrl}/api/groups/${groupId}`);
  }

  getPendingInvites(): Observable<PendingGroupInvite[]> {
    return this.http.get<PendingGroupInvite[]>(`${this.baseUrl}/api/groups/invites`);
  }

  createGroup(name: string, makeDefault = false): Observable<ShoppingGroupDetails> {
    return this.http.post<ShoppingGroupDetails>(`${this.baseUrl}/api/groups`, {
      name,
      makeDefault
    }).pipe(
      tap(() => void this.checkSession())
    );
  }

  renameGroup(groupId: number, name: string): Observable<ShoppingGroupDetails> {
    return this.http.patch<ShoppingGroupDetails>(`${this.baseUrl}/api/groups/${groupId}`, {
      name
    });
  }

  deleteGroup(groupId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/api/groups/${groupId}`).pipe(
      tap(() => void this.checkSession())
    );
  }

  inviteMember(
    groupId: number,
    email: string,
    role = 'editor',
    basketId?: number | null,
    page?: string | null
  ): Observable<ShoppingGroupDetails> {
    const payload: { email: string; role: string; basketId?: number; page?: string } = {
      email,
      role
    };
    if (basketId != null) {
      payload.basketId = basketId;
    }
    if (page) {
      payload.page = page;
    }
    return this.http.post<ShoppingGroupDetails>(
      `${this.baseUrl}/api/groups/${groupId}/invites`,
      payload
    );
  }

  getGroupInviteToken(groupId: number): Observable<{ token: string; groupId: number }> {
    return this.http.post<{ token: string; groupId: number }>(
      `${this.baseUrl}/api/groups/${groupId}/invite-token`,
      {}
    );
  }

  joinGroupByToken(token: string): Observable<ShoppingGroupDetails> {
    return this.http.post<ShoppingGroupDetails>(`${this.baseUrl}/api/groups/join`, { token }).pipe(
      tap(() => void this.checkSession())
    );
  }

  acceptInvite(groupId: number): Observable<ShoppingGroupDetails> {
    return this.http.post<ShoppingGroupDetails>(`${this.baseUrl}/api/groups/${groupId}/accept`, {}).pipe(
      tap(() => void this.checkSession())
    );
  }

  declineInvite(groupId: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/api/groups/${groupId}/decline`, {});
  }

  removeMember(groupId: number, targetUserId: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/groups/${groupId}/members/${targetUserId}/remove`,
      {}
    ).pipe(
      tap(() => void this.checkSession())
    );
  }

  setDefaultGroup(groupId: number): Observable<{ defaultGroupId: number | null }> {
    return this.http.post<{ defaultGroupId: number | null }>(
      `${this.baseUrl}/api/groups/${groupId}/set-default`,
      {}
    ).pipe(
      tap((res) => {
        if (this.currentUser) {
          this.currentUserSubject.next({
            ...this.currentUser,
            defaultGroupId: res.defaultGroupId
          });
        }
      })
    );
  }

  // ── Multi-Basket Management ──────────────────────────────────────────

  getUserBaskets(): Observable<BasketSummary[]> {
    return this.http.get<BasketSummary[]>(`${this.baseUrl}/api/v2/baskets`);
  }

  createBasket(name?: string, groupId?: number | null): Observable<ShoppingBasket> {
    return this.http.post<ShoppingBasket>(`${this.baseUrl}/api/v2/baskets`, {
      name: name ?? null,
      groupId: groupId ?? null
    }).pipe(
      tap((basket) => {
        this.localProfile.setShoppingBasketId(basket.id);
        if (this.currentUser) {
          this.currentUserSubject.next({
            ...this.currentUser,
            currentBasketId: basket.id
          });
        }
      })
    );
  }

  closeBasket(basketId: number): Observable<ShoppingBasket> {
    return this.http.post<ShoppingBasket>(`${this.baseUrl}/api/v2/baskets/${basketId}/close`, {});
  }

  openBasket(basketId: number): Observable<ShoppingBasket> {
    return this.http.post<ShoppingBasket>(`${this.baseUrl}/api/v2/baskets/${basketId}/open`, {});
  }

  deleteBasket(basketId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/api/v2/baskets/${basketId}`).pipe(
      tap(() => void this.checkSession())
    );
  }

  setBasketGroup(basketId: number, groupId: number | null): Observable<ShoppingBasket> {
    return this.http.patch<ShoppingBasket>(`${this.baseUrl}/api/v2/baskets/${basketId}/group`, {
      groupId
    });
  }

  renameBasket(basketId: number, name: string): Observable<ShoppingBasket> {
    return this.http.patch<ShoppingBasket>(`${this.baseUrl}/api/v2/baskets/${basketId}/name`, {
      name
    });
  }

  selectBasket(basketId: number): Observable<{ currentBasketId: number }> {
    return this.http.post<{ currentBasketId: number }>(`${this.baseUrl}/api/v2/baskets/${basketId}/select`, {}).pipe(
      tap(() => {
        this.localProfile.setShoppingBasketId(basketId);
        if (this.currentUser) {
          this.currentUserSubject.next({
            ...this.currentUser,
            currentBasketId: basketId
          });
        }
      })
    );
  }
}
